import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CatalogueApiService,
  OgcApiService,
  type OgcCollectionLink,
  type OgcConfiguredS3Bucket,
  type OgcProcessSummary,
} from '@angular-workspace/services';
import type { OgcUploadedFileItem } from './ogc-upload.models';
import { resolveUploadedObjectKey } from './resolve-uploaded-object-key';
import { findExistingGeoPackageLink } from './find-existing-geopackage';

const PROCESS_TITLES = {
  preSigned: 'S3PreSignedURLGeneration',
  initiateMultipart: 'S3InitiateMultipartUpload',
  completeMultipart: 'S3CompleteMultipartUpload',
  collectionOnboarding: 'CollectionOnboarding',
} as const;

@Component({
  selector: 'lib-ogc-upload-files',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
})
export class OgcUploadFilesComponent {
  private readonly ogcApi = inject(OgcApiService);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly itemId = input<string | null>(null);
  readonly datasetTitle = input('');
  readonly datasetDescription = input('');
  readonly showModalPreviousButton = input(true);
  /** Parent increments when the upload modal opens so GET /processes runs each time. */
  readonly loadKey = input(0);

  readonly modalDismiss = output<void>();
  readonly modalPrevious = output<void>();
  readonly publishSuccess = output<void>();
  readonly uploadError = output<string>();

  readonly selectedFiles = signal<File[]>([]);
  readonly uploadedFiles = signal<OgcUploadedFileItem[]>([]);
  readonly isDropzoneActive = signal(false);
  readonly processesLoading = signal(false);
  readonly processesLoadError = signal<string | null>(null);
  readonly processesReady = signal(false);
  readonly uploading = signal(false);
  readonly banner = signal<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );

  readonly buckets = signal<OgcConfiguredS3Bucket[]>([]);
  readonly bucketsLoading = signal(false);
  readonly selectedBucketId = signal('');
  readonly storageDropdownOpen = signal(false);

  readonly existingGeoPackage = signal<OgcCollectionLink | null>(null);
  readonly existingGeoPackageLoading = signal(false);

  private dropZoneDepth = 0;
  private processes: OgcProcessSummary[] = [];
  private processIds: Partial<Record<keyof typeof PROCESS_TITLES, string>> = {};

  readonly canFooterPrimary = computed(() => {
    if (
      this.uploading() ||
      this.processesLoading() ||
      this.bucketsLoading() ||
      this.processesLoadError() ||
      !this.processesReady() ||
      !this.selectedBucketId().trim()
    ) {
      return false;
    }
    if (this.selectedFiles().length > 0) return true;
    return this.uploadedFiles().length > 0 && this.isAllUploadsCompleted();
  });

  readonly clearSelectedFilesLabel = computed(() =>
    this.selectedFiles().length === 1 ? 'Clear' : 'Clear All',
  );

  readonly footerPrimaryLabel = computed(() => {
    if (this.selectedFiles().length > 0) {
      if (this.uploading()) return 'Uploading…';
      return this.selectedFiles().length === 1 ? 'Upload' : 'Upload All';
    }
    if (this.uploadedFiles().length > 0 && this.isAllUploadsCompleted()) {
      return 'Publish';
    }
    return 'Upload';
  });

  readonly canSelectFiles = computed(
    () =>
      !!this.selectedBucketId().trim() &&
      this.processesReady() &&
      !this.processesLoading() &&
      !this.bucketsLoading() &&
      !this.uploading() &&
      this.uploadedFiles().length === 0 &&
      this.selectedFiles().length === 0 &&
      !this.existingGeoPackage(),
  );

  /** Vector onboarding is one GeoPackage — hide picker once a file is chosen or already exists. */
  readonly showUploadPicker = computed(
    () =>
      this.uploadedFiles().length === 0 &&
      this.selectedFiles().length === 0 &&
      !this.existingGeoPackage(),
  );

  /** Shown only before the dataset's first-ever GeoPackage upload — onboarding is a one-time, irreversible action. */
  readonly showOneTimeUploadNotice = computed(
    () =>
      !this.existingGeoPackageLoading() &&
      !this.existingGeoPackage() &&
      this.uploadedFiles().length === 0,
  );

  /** Hide Previous once the user has started uploading (one-time GeoPackage flow). */
  readonly showPreviousButton = computed(
    () => this.showModalPreviousButton() && this.uploadedFiles().length === 0,
  );

  modalFormatLabel(): string {
    return 'GPKG';
  }

  private loadInFlight: Promise<void> | null = null;

  constructor() {
    effect(() => {
      const key = this.loadKey();
      if (key < 1) {
        return;
      }
      void this.loadProcesses();
      void this.loadBuckets();
      void this.loadExistingGeoPackage();
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.storageDropdownOpen.set(false);
  }

  async loadProcesses(): Promise<void> {
    if (this.loadInFlight) {
      await this.loadInFlight;
      return;
    }

    this.processesLoading.set(true);
    this.processesLoadError.set(null);
    this.processesReady.set(false);

    this.loadInFlight = (async () => {
    try {
      const list = await this.ogcApi.getProcesses();
      this.processes = list.processes ?? [];
      this.processIds = {
        preSigned:
          this.ogcApi.findProcessIdByTitle(
            this.processes,
            PROCESS_TITLES.preSigned,
          ) ?? undefined,
        initiateMultipart:
          this.ogcApi.findProcessIdByTitle(
            this.processes,
            PROCESS_TITLES.initiateMultipart,
          ) ?? undefined,
        completeMultipart:
          this.ogcApi.findProcessIdByTitle(
            this.processes,
            PROCESS_TITLES.completeMultipart,
          ) ?? undefined,
        collectionOnboarding:
          this.ogcApi.findProcessIdByTitle(
            this.processes,
            PROCESS_TITLES.collectionOnboarding,
          ) ?? undefined,
      };
      const missing: string[] = [];
      if (!this.processIds.preSigned) {
        missing.push('S3PreSignedURLGeneration');
      }
      if (!this.processIds.collectionOnboarding) {
        missing.push('CollectionOnboarding');
      }
      if (missing.length > 0) {
        this.processesLoadError.set(
          `${missing.join(' and ')} process(es) were not found. Please try again later.`,
        );
      } else {
        this.processesReady.set(true);
      }
    } catch (error) {
      const message = this.errorMessage(error);
      this.processesLoadError.set(message);
      this.uploadError.emit(message);
    } finally {
      this.processesLoading.set(false);
      this.loadInFlight = null;
    }
    })();

    await this.loadInFlight;
  }

  async loadBuckets(): Promise<void> {
    try {
      this.bucketsLoading.set(true);
      const buckets = await this.ogcApi.listConfiguredS3Buckets();
      const privateBuckets = buckets.filter(
        (bucket) => String(bucket.readAccess ?? '').toUpperCase() === 'PRIVATE',
      );
      this.buckets.set(privateBuckets);
      if (privateBuckets.length > 0 && !this.selectedBucketId().trim()) {
        this.selectedBucketId.set(this.bucketIdentifier(privateBuckets[0]!));
      }
    } catch (error) {
      const message = this.errorMessage(error);
      this.showBanner('error', message);
      this.uploadError.emit(message);
      this.buckets.set([]);
    } finally {
      this.bucketsLoading.set(false);
    }
  }

  /** GET {ogcBase}/collections/{itemId} — surface a GeoPackage already onboarded for this dataset. */
  async loadExistingGeoPackage(): Promise<void> {
    const collectionId = (this.itemId() ?? '').trim();
    if (!collectionId) {
      this.existingGeoPackage.set(null);
      return;
    }
    try {
      this.existingGeoPackageLoading.set(true);
      const collection = await this.ogcApi.getCollection(collectionId);
      this.existingGeoPackage.set(
        findExistingGeoPackageLink(collection?.links),
      );
    } catch {
      // ponytail: best-effort lookup — upload still works if this fails, just skip the banner
      this.existingGeoPackage.set(null);
    } finally {
      this.existingGeoPackageLoading.set(false);
    }
  }

  private async ensureProcessesLoaded(): Promise<boolean> {
    if (this.processesReady()) {
      return true;
    }
    if (this.processesLoadError()) {
      return false;
    }
    await this.loadProcesses();
    return this.processesReady();
  }

  hasFilesToShow(): boolean {
    return this.selectedFiles().length > 0 || this.uploadedFiles().length > 0;
  }

  selectedFilesTitle(): string {
    const selected = this.selectedFiles().length;
    const uploaded = this.uploadedFiles().length;
    if (uploaded === 0) return `Selected Files (${selected})`;
    if (selected === 0) return `Uploaded Files (${uploaded})`;
    return 'Files';
  }

  isAllUploadsCompleted(): boolean {
    const files = this.uploadedFiles();
    return files.length > 0 && files.every((f) => f.status === 'completed');
  }

  onModalPrevious(): void {
    this.modalPrevious.emit();
  }

  onModalDismiss(): void {
    this.modalDismiss.emit();
  }

  openFilePicker(input: HTMLInputElement): void {
    if (!this.canSelectFiles()) {
      if (!this.selectedBucketId().trim()) {
        this.showBanner('error', 'Select storage before uploading files.');
      }
      return;
    }
    input.click();
  }

  onFileSelected(event: Event): void {
    if (!this.canSelectFiles()) {
      (event.target as HTMLInputElement).value = '';
      return;
    }
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.ingestFiles(Array.from(input.files));
    input.value = '';
  }

  onDropZoneDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.canSelectFiles() ? 'copy' : 'none';
    }
  }

  onDropZoneDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canSelectFiles()) return;
    this.dropZoneDepth++;
    this.isDropzoneActive.set(true);
  }

  onDropZoneDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneDepth = Math.max(0, this.dropZoneDepth - 1);
    if (this.dropZoneDepth === 0) {
      this.isDropzoneActive.set(false);
    }
  }

  onDropZoneDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneDepth = 0;
    this.isDropzoneActive.set(false);
    if (!this.canSelectFiles()) {
      this.showBanner('error', 'Select storage before uploading files.');
      return;
    }
    const list = event.dataTransfer?.files;
    if (!list?.length) return;
    this.ingestFiles(Array.from(list));
  }

  dismissBanner(): void {
    this.banner.set(null);
  }

  getFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  removeSelectedFile(index: number): void {
    const next = [...this.selectedFiles()];
    next.splice(index, 1);
    this.selectedFiles.set(next);
  }

  deleteUploadedFile(fileItem: OgcUploadedFileItem): void {
    // One-time vector upload — completed GeoPackages cannot be removed.
    if (fileItem.status === 'completed') {
      return;
    }
    this.uploadedFiles.update((list) =>
      list.filter(
        (f) =>
          f.file.name !== fileItem.file.name || f.file.size !== fileItem.file.size,
      ),
    );
  }

  clearAllSelected(): void {
    this.selectedFiles.set([]);
  }

  bucketIdentifier(bucket: OgcConfiguredS3Bucket): string {
    return String(bucket.s3BucketIdentifier ?? bucket.bucketName ?? '').trim();
  }

  toggleStorageDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.storageDropdownOpen.update((open) => !open);
  }

  selectStorageBucket(bucketId: string): void {
    this.selectedBucketId.set(bucketId);
    this.storageDropdownOpen.set(false);
  }

  getSelectedBucketLabel(): string {
    const id = this.selectedBucketId();
    const bucket = this.buckets().find(
      (entry) => this.bucketIdentifier(entry) === id,
    );
    if (!bucket) {
      return id;
    }
    return `${this.bucketIdentifier(bucket)} (${bucket.readAccess})`;
  }

  async onFooterPrimaryClick(): Promise<void> {
    if (this.selectedFiles().length > 0) {
      if (!this.selectedBucketId().trim()) {
        this.showBanner('error', 'Select storage before uploading files.');
        return;
      }
      if (!(await this.ensureProcessesLoaded())) return;
      await this.uploadAllFiles();
      return;
    }
    if (this.isAllUploadsCompleted()) {
      this.publishSuccess.emit();
    }
  }

  async uploadSingleFileFromSelected(index: number): Promise<void> {
    if (
      this.uploading() ||
      index < 0 ||
      index >= this.selectedFiles().length ||
      !this.selectedBucketId().trim() ||
      !(await this.ensureProcessesLoaded())
    ) {
      return;
    }
    const files = [...this.selectedFiles()];
    const file = files[index];
    files.splice(index, 1);
    this.selectedFiles.set(files);

    this.ensureUploadedEntry(file);
    this.uploading.set(true);
    try {
      await this.uploadSingleFile(file);
      const latest = this.findUploadedItem(file);
      if (latest?.status === 'completed') {
        this.showBanner('success', `${file.name} uploaded successfully.`);
      } else if (latest?.error) {
        this.showBanner('error', latest.error);
      }
    } finally {
      this.uploading.set(false);
    }
  }

  async uploadAllFiles(): Promise<void> {
    if (this.uploading() || this.selectedFiles().length === 0) return;
    if (!this.selectedBucketId().trim()) {
      this.showBanner('error', 'Select storage before uploading files.');
      return;
    }
    if (!(await this.ensureProcessesLoaded())) return;

    const toUpload = [...this.selectedFiles()];
    for (const file of toUpload) {
      this.ensureUploadedEntry(file);
    }
    this.selectedFiles.set([]);

    this.uploading.set(true);
    try {
      const pending = this.uploadedFiles().filter(
        (f) => f.status === 'pending' || f.status === 'error',
      );
      for (const fileItem of pending) {
        await this.uploadSingleFile(fileItem.file);
      }
      if (this.isAllUploadsCompleted()) {
        this.showBanner('success', 'All GeoPackage files uploaded successfully.');
      }
    } finally {
      this.uploading.set(false);
    }
  }

  private ensureUploadedEntry(file: File): void {
    this.uploadedFiles.update((list) => {
      if (list.some((f) => f.file.name === file.name && f.file.size === file.size)) {
        return list;
      }
      return [...list, { file, progress: 0, status: 'pending' }];
    });
  }

  private ingestFiles(newFiles: File[]): void {
    if (!this.showUploadPicker()) {
      return;
    }
    const gpkg = newFiles.find((file) => this.isGpkgFile(file));
    const rejectedCount = newFiles.length - (gpkg ? 1 : 0);
    if (!gpkg) {
      this.showBanner(
        'error',
        `Only GeoPackage (.gpkg) files can be added. ${rejectedCount} file(s) were not added.`,
      );
      return;
    }
    // One-time vector upload: keep a single pending file (replace any prior selection).
    this.selectedFiles.set([gpkg]);
    if (rejectedCount > 0) {
      this.showBanner(
        'error',
        `Only one GeoPackage (.gpkg) file can be uploaded. ${rejectedCount} file(s) were not added.`,
      );
    }
  }

  private isGpkgFile(file: File): boolean {
    return file.name.toLowerCase().endsWith('.gpkg');
  }

  private async uploadSingleFile(file: File): Promise<void> {
    const resourceId = (this.itemId() ?? '').trim();
    const preSignedProcessId = this.processIds.preSigned;
    const bucketId = this.selectedBucketId().trim();
    if (!resourceId || !preSignedProcessId) {
      this.patchUploadedFile(file, {
        status: 'error',
        error: 'Dataset ID or upload configuration is missing.',
      });
      return;
    }
    if (!bucketId) {
      this.patchUploadedFile(file, {
        status: 'error',
        error: 'Select storage before uploading files.',
      });
      return;
    }

    try {
      this.patchUploadedFile(file, {
        status: 'uploading',
        progress: 0,
        error: undefined,
      });

      const useMultipart =
        !!this.processIds.initiateMultipart &&
        !!this.processIds.completeMultipart &&
        file.size > 5 * 1024 * 1024;

      const s3ObjectKeyName = useMultipart
        ? await this.uploadViaMultipart(file, resourceId, bucketId)
        : await this.uploadViaPreSignedUrl(
            file,
            resourceId,
            preSignedProcessId,
            bucketId,
          );

      this.patchUploadedFile(file, { s3ObjectKeyName });
      await this.markDataUploadStatus(resourceId);
      await this.executeCollectionOnboardingAfterS3Upload(
        file,
        resourceId,
        s3ObjectKeyName,
        bucketId,
      );

      this.patchUploadedFile(file, { status: 'completed', progress: 100 });
    } catch (error) {
      const message = this.errorMessage(error);
      this.patchUploadedFile(file, { status: 'error', error: message, progress: 0 });
      this.uploadError.emit(message);
    }
  }

  /**
   * PATCH catalogue item `dataUploadStatus: true` once the GeoPackage has landed on S3.
   * Never set on item creation — only here, right after a successful upload.
   */
  private async markDataUploadStatus(itemId: string): Promise<void> {
    try {
      await this.catalogueApi.postPublishAssets(itemId, {
        dataUploadStatus: true,
      });
    } catch (error) {
      console.error('Failed to set dataUploadStatus:', error);
    }
  }

  /**
   * After S3 upload:
   * 1. POST {ogcDomain}/processes/{CollectionOnboarding-id}/execution → jobId
   * 2. GET {ogcDomain}/jobs/{jobId} until the job completes
   */
  private async executeCollectionOnboardingAfterS3Upload(
    file: File,
    resourceId: string,
    s3ObjectKeyName: string,
    s3BucketIdentifier: string,
  ): Promise<void> {
    const processId = this.processIds.collectionOnboarding?.trim();
    if (!processId) {
      throw new Error(
        'CollectionOnboarding process was not found. Cannot complete OGC onboarding.',
      );
    }

    this.patchUploadedFile(file, { status: 'onboarding', progress: 95 });

    await this.ogcApi.runCollectionOnboardingAndWaitForJob({
      processId,
      title: this.datasetTitle().trim() || file.name,
      fileName: s3ObjectKeyName,
      resourceId,
      description: this.datasetDescription().trim(),
      s3BucketIdentifier,
    });
  }

  private async uploadViaPreSignedUrl(
    file: File,
    resourceId: string,
    preSignedProcessId: string,
    s3BucketIdentifier: string,
  ): Promise<string> {
    const presigned = await this.ogcApi.generatePreSignedUrl({
      processId: preSignedProcessId,
      resourceId,
      s3BucketIdentifier,
    });
    const url = presigned.S3PreSignedUrl?.trim();
    if (!url) {
      throw new Error(
        presigned.message?.trim() ||
          'Pre-signed URL was not returned by the server.',
      );
    }
    await this.ogcApi.uploadToPresignedUrl(url, file, (pct) =>
      this.patchUploadedFile(file, { progress: pct }),
    );
    return resolveUploadedObjectKey(presigned.s3ObjectKeyName, file.name);
  }

  private async uploadViaMultipart(
    file: File,
    resourceId: string,
    s3BucketIdentifier: string,
  ): Promise<string> {
    const initiateId = this.processIds.initiateMultipart!;
    const completeId = this.processIds.completeMultipart!;

    const initiated = await this.ogcApi.initiateMultipartUpload({
      processId: initiateId,
      resourceId,
      fileName: file.name,
      fileSize: file.size,
      collectionId: resourceId,
      s3BucketIdentifier,
    });

    const uploadId = initiated.uploadId?.trim();
    const chunkSize = initiated.chunkSize ?? 5 * 1024 * 1024;
    const presignedUrls = initiated.presignedUrls ?? [];
    const s3ObjectKeyName = resolveUploadedObjectKey(
      initiated.s3KeyName,
      file.name,
    );

    if (!uploadId || presignedUrls.length === 0) {
      throw new Error('Multipart upload could not be initiated.');
    }

    const sortedParts = [...presignedUrls].sort(
      (a, b) => a.partNumber - b.partNumber,
    );
    const completedPartTags: string[] = [];

    for (let i = 0; i < sortedParts.length; i++) {
      const part = sortedParts[i];
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const etag = await this.ogcApi.uploadToPresignedUrl(
        part.url,
        chunk,
        (pct) => {
          const overall = Math.round(
            ((i + pct / 100) / sortedParts.length) * 100,
          );
          this.patchUploadedFile(file, { progress: overall });
        },
      );
      completedPartTags.push(`${part.partNumber}:${etag}`);
    }

    await this.ogcApi.completeMultipartUpload({
      processId: completeId,
      resourceId,
      uploadId,
      filePath: s3ObjectKeyName,
      parts: completedPartTags,
      s3BucketIdentifier,
    });

    return s3ObjectKeyName;
  }

  private patchUploadedFile(
    file: File,
    patch: Partial<OgcUploadedFileItem>,
  ): void {
    this.uploadedFiles.update((list) =>
      list.map((f) =>
        f.file.name === file.name && f.file.size === file.size
          ? { ...f, ...patch }
          : f,
      ),
    );
  }

  private findUploadedItem(file: File): OgcUploadedFileItem | undefined {
    return this.uploadedFiles().find(
      (f) => f.file.name === file.name && f.file.size === file.size,
    );
  }

  private showBanner(type: 'success' | 'error', message: string): void {
    this.banner.set({ type, message });
  }

  private errorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as {
        error?: { description?: string; message?: string; detail?: string };
        message?: string;
        statusText?: string;
      };
      const fromBody =
        err.error?.description ??
        err.error?.detail ??
        err.error?.message ??
        err.message ??
        err.statusText;
      if (typeof fromBody === 'string' && fromBody.trim()) {
        return fromBody.trim();
      }
    }
    return 'OGC upload failed. Please try again.';
  }
}
